import React, { Fragment, memo, useEffect, useRef } from "react";
import { Divider } from "@bookingcom/bui-react";
import { useSelector } from "react-redux";
import useBrandedFares, { useBrandedFaresComparisonTableCompact } from "../../../hooks/useBrandedFares.js";
import Frame from "../../../../components/elements/Frame/index.js";
import useTrackFirstFarePriceMismatch from "../../../hooks/useTrackFirstFarePriceMismatch.js";
import { isDeltaAirlinesOffer } from "../../../../components/elements/FlightCard/utils.js";
import useIsInViewport from "../../../../hooks/useIsInViewport.js";
import flights_apex_web_delta_airlines_pilot_2026 from "../../../../utils/experiments/apex/flights_apex_web_delta_airlines_pilot_2026.js";
import flights_web_delta_airlines_pilot_phase_2 from "../../../../utils/experiments/apex/flights_web_delta_airlines_pilot_phase_2.js";
import FareRowsDesktop from "./FareRows.desktop.js";
import { SkeletonFareRow } from "./SkeletonFareRow.desktop.js";
import useClientMetrics from "../../../../hooks/useClientMetrics.js";
import useTimer from "../../../../hooks/useTimer.js";
import { trackBrandedFareFeaturesOnBackendStages } from "../../../../utils/experiments/apex/flights_apex_web_filter_branded_fare_features_on_backend.js";
import { trackAlignedBrandedFareFeaturesStages } from "../../../../utils/experiments/apex/flights_apex_web_filter_align_branded_fare_features_apps.js";
import { findFareIndex } from "../../../utils/fares.js";
import { BrandedFaresComparisonTable } from "./BrandedFaresComparisonTable.js";
import { getSearch } from "../../../../store/search/selectors.js";
import flights_apex_tabular_branded_fares_sr from "../../../../utils/experiments/apex/flights_apex_tabular_branded_fares_sr.js";
const _FareSelectorTabularDesktop = ({ flight, variant }) => {
    const { fetchStatus, brandedFareOffers } = useBrandedFares(flight.token);
    const brandedFaresComparisonTable = useBrandedFaresComparisonTableCompact(flight.token);
    const faresAvailableFromBrandedFares = !!brandedFareOffers && brandedFareOffers?.length > 0;
    const containerRef = useRef(null);
    const search = useSelector(getSearch);
    const isMultiTraveller = (search.adults || 0) + (search.children || []).length > 1;
    useTrackFirstFarePriceMismatch(flight, brandedFareOffers);
    const brandedFareOffersToRender = faresAvailableFromBrandedFares ? brandedFareOffers : flight.brandedFareOffers;
    const hasBrandedFareOffers = (brandedFareOffersToRender?.length ?? 0) > 0;
    useEffect(() => {
        if (!hasBrandedFareOffers)
            return;
        trackBrandedFareFeaturesOnBackendStages("search_results", flight.segments);
        trackAlignedBrandedFareFeaturesStages("search_results", flight.segments);
    }, [flight.brandedFareInfo, flight.segments, hasBrandedFareOffers]);
    const remainingBrandedFares = brandedFareOffersToRender?.slice(0, 3) || [];
    const hasRemainingFares = remainingBrandedFares.length > 0;
    const getTablePopoverContent = (fareIndex) => brandedFareOffersToRender && brandedFaresComparisonTable ? (React.createElement(BrandedFaresComparisonTable, { brandedFaresComparisonTable: brandedFaresComparisonTable, highlightedFareIndex: fareIndex, isMultiTraveller: isMultiTraveller, currencyCode: flight.priceBreakdown.total.currencyCode })) : undefined;
    useEffect(() => {
        if (isMultiTraveller) {
            flights_apex_tabular_branded_fares_sr.stages.multitravellers();
        }
    }, [isMultiTraveller]);
    useEffect(() => {
        const hasDuplicateBaggageCodes = (featuresList) => {
            const baggageCodes = featuresList.filter((f) => f.category === "BAGGAGE").map((f) => f.code);
            return baggageCodes.length !== new Set(baggageCodes).size;
        };
        const variantHasDuplicates = brandedFareOffersToRender?.some((offer) => hasDuplicateBaggageCodes(offer.brandedFareInfo?.featuresList ?? []));
        if (variantHasDuplicates) {
            flights_apex_tabular_branded_fares_sr.stages.different_baggage_allowance();
        }
    }, [brandedFareOffersToRender]);
    const isInViewport = useIsInViewport(containerRef, [flight, brandedFareOffers], { threshold: 0.8 });
    const isDelta = isDeltaAirlinesOffer(flight);
    const startTime = useRef(null);
    useEffect(() => {
        if (!isDelta)
            return;
        if (isInViewport && fetchStatus === "success" && !hasBrandedFareOffers) {
            flights_apex_web_delta_airlines_pilot_2026.goals.empty_delta_fares_seen();
        }
        if (fetchStatus === "loading" && isInViewport && !startTime.current) {
            flights_web_delta_airlines_pilot_phase_2.goals.delta_fares_loader_seen();
            startTime.current = Date.now();
        }
        if (fetchStatus === "success" && startTime.current) {
            const duration = Date.now() - startTime.current;
            flights_apex_web_delta_airlines_pilot_2026.goalsWithValue.flights_delta_fares_loader_seen_ms(duration);
            startTime.current = null;
        }
    }, [fetchStatus, isDelta, isInViewport, hasBrandedFareOffers]);
    const { trackClientMetric } = useClientMetrics();
    const loaderTimer = useTimer();
    useEffect(() => {
        if (fetchStatus === "loading" && isInViewport) {
            loaderTimer.start();
        }
        if (fetchStatus === "success") {
            const duration = loaderTimer.stop();
            if (duration)
                trackClientMetric("branded_fares_on_sr_loader_seen_ms", duration);
        }
    }, [fetchStatus, isInViewport, trackClientMetric, loaderTimer]);
    return (React.createElement("div", { ref: containerRef },
        React.createElement(Divider, null),
        React.createElement(Frame, { direction: "column", attributes: {
                style: { maxWidth: "100%" }
            } }, hasRemainingFares ? (React.createElement(React.Fragment, null, remainingBrandedFares.map((brandedFareOffer, i) => {
            const sourceOffers = brandedFareOffers ?? flight.brandedFareOffers;
            const globalIndex = findFareIndex(brandedFareOffer.token, sourceOffers);
            return (React.createElement(Fragment, { key: brandedFareOffer.token },
                React.createElement(FareRowsDesktop, { baseOffer: flight, fareOffer: brandedFareOffer, index: globalIndex, numberOfOffers: brandedFareOffersToRender?.length ?? 0, variant: variant, customPopoverContent: getTablePopoverContent(i), numberOfFaresInPopover: remainingBrandedFares.length }),
                remainingBrandedFares[remainingBrandedFares.length - 1]?.token !== brandedFareOffer.token ? (React.createElement(Divider, null)) : null));
        }))) : hasBrandedFareOffers ? null : (React.createElement(React.Fragment, null, fetchStatus === "loading" && React.createElement(SkeletonFareRow, null))))));
};
export const FareSelectorTabularDesktop = memo(_FareSelectorTabularDesktop);
